import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, ShieldCheck, ShieldAlert, CheckCircle2, XCircle, Lock } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { api } from '../../lib/api';
import { toast } from 'sonner';

export default function AdminPrivacy() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/admin/privacy-audit').then((r) => setData(r.data)).catch(() => toast.error('Failed to load audit'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.put('/admin/launch-checklist', { key: 'privacy_confirmed', done: true });
      toast.success('Privacy confirmed — checklist updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="admin-privacy">
      <h1 className="font-serif font-black text-2xl text-[#1a2744] mb-1">Public Privacy Audit</h1>
      <p className="text-sm text-slate-500 mb-4">A live inspection of exactly which issue-report fields appear on each public surface. Run against real data every time you open this page.</p>

      <Card className={`mb-5 ${data.overall_pass ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}>
        <CardContent className="p-5 flex items-center gap-3">
          {data.overall_pass ? <ShieldCheck className="h-8 w-8 text-emerald-700 shrink-0" /> : <ShieldAlert className="h-8 w-8 text-red-700 shrink-0" />}
          <div className="flex-1">
            <p className={`font-black ${data.overall_pass ? 'text-emerald-900' : 'text-red-900'}`} data-testid="privacy-overall">
              {data.overall_pass ? 'PASS — no private resident fields appear publicly' : 'FAIL — private data may be exposed. Do not launch.'}
            </p>
            <p className="text-xs text-slate-600 mt-0.5">Checked {new Date(data.checked_at).toLocaleString()}</p>
          </div>
          {data.overall_pass && (
            <Button onClick={confirm} disabled={busy} className="h-11 bg-[#1a2744] text-white font-bold btn-press shrink-0" data-testid="privacy-confirm-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />} Confirm Privacy
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="space-y-4">
        {data.surfaces.map((s) => (
          <Card key={s.surface} data-testid="privacy-surface-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="font-serif font-bold text-[#1a2744]">{s.surface}</p>
                {s.pass ? (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 font-bold"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> No private fields</Badge>
                ) : (
                  <Badge variant="outline" className="bg-red-50 text-red-800 border-red-200 font-bold"><XCircle className="h-3.5 w-3.5 mr-1" /> PRIVATE DATA EXPOSED</Badge>
                )}
              </div>
              <p className="text-xs font-mono text-slate-400 mt-1">{s.endpoint}</p>
              <div className="mt-3">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Fields shown publicly</p>
                <div className="flex flex-wrap gap-1.5">
                  {s.exposed_fields.map((f) => (
                    <span key={f} className="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-full text-xs font-semibold text-slate-700">{f}</span>
                  ))}
                </div>
              </div>
              {s.private_fields_present.length > 0 && (
                <p className="text-sm font-bold text-red-700 mt-3">Private fields leaking: {s.private_fields_present.join(', ')}</p>
              )}
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">{s.notes}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mt-5">
        <CardContent className="p-5">
          <p className="font-serif font-bold text-[#1a2744] mb-2">Fields that are never shown publicly</p>
          <ul className="space-y-1.5">
            {data.private_fields_policy.map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-slate-700"><Lock className="h-3.5 w-3.5 text-[#C9A84C] shrink-0" /> {f}</li>
            ))}
          </ul>
          <p className="text-xs text-slate-500 mt-3 leading-relaxed">{data.uploads_note}</p>
        </CardContent>
      </Card>
    </div>
  );
}
